/**
 * Tenant picker in the sidebar footer.
 *
 * Backed by real memberships now: selecting one changes the active tenant in
 * `TenantContext`, which is what every tenant-scoped request and every permission
 * check reads. A user in exactly one workspace still sees the control — it shows
 * them which workspace they are in, which is worth a row even when there is no
 * choice to make.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useTranslation } from '../../context/LanguageContext';
import { useTenant } from '../../context/TenantContext';
import { workspaceAccentClass, workspaceInitials } from '../../config/workspaces';
import type { TenantMembershipSummary } from '@app/common';

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { memberships, active, loading, setActiveTenantId } = useTenant();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // A dropdown anchored in the sidebar overlaps the page, so it has to close on
  // an outside click or it stays over content the user is trying to reach.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // A user who belongs to no workspace yet — invited but not placed, or a
  // superadmin who administers tenants without being in one — gets a plain label
  // rather than an empty dropdown that does nothing when clicked.
  if (loading || !active) {
    return (
      <div className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2">
        <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {t('workspace.label')}
        </span>
        <span className="block truncate text-[13px] font-semibold text-slate-400">
          {loading ? t('common.loading') : t('workspace.none')}
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
      >
        <Monogram membership={active} />
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {t('workspace.label')}
          </span>
          <span className="block truncate text-[13px] font-semibold text-slate-800">
            {active.tenant.name}
          </span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute bottom-full left-0 z-30 mb-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10"
        >
          {memberships.map((membership) => {
            const isActive = membership.tenant.id === active.tenant.id;
            return (
              <button
                key={membership.tenant.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setActiveTenantId(membership.tenant.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition-colors hover:bg-slate-50"
              >
                <Monogram membership={membership} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-slate-800">
                    {membership.tenant.name}
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    {t('workspace.members', { count: membership.memberCount })}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                    {t(
                      membership.role === 'admin' ? 'workspace.roleAdmin' : 'workspace.roleMember'
                    )}
                  </span>
                  {isActive && <Check className="h-3.5 w-3.5 text-blue-600" />}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Monogram({ membership }: Readonly<{ membership: TenantMembershipSummary }>) {
  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-[10px] font-bold text-white',
        workspaceAccentClass(membership.tenant.id)
      )}
    >
      {workspaceInitials(membership.tenant.name)}
    </span>
  );
}
